import { Box, CircularProgress, IconButton, Skeleton } from '@mui/material';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import HelpIcon from '@mui/icons-material/Help';
import { useAudio } from '@/hooks/useAudio';
import { useCountUp } from 'use-count-up';
import { useEffect } from 'react';

const ICON_SIZE = {
    width: 160,
    height: 160,
    // please adjust progress accordingly based on width and height above
    progress: 134,
};

/**
 * MUI CircularProgress component's value is 0 to 100.
 * Scale up the counter so we have a smoother animation effect as it takes decimal values
 */
const PROGRESS_LACING_RATE = 100;

export function ResultDisplayRecording({ src }: { src?: string }) {
    // only care about the latest answer for now
    const { toggle, stop, playing, duration } = useAudio(src);
    const { value: progress, reset } = useCountUp({
        isCounting: playing,
        duration: duration,
        start: 0,
        end: 100 * PROGRESS_LACING_RATE,
        easing: 'linear',
    });

    useEffect(() => {
        if (!playing) {
            reset();
        }
    }, [playing, reset]);

    useEffect(() => {
        return () => {
            stop();
        };
    }, [stop]);

    return (
        <Box sx={{ position: 'relative', display: 'inline-flex', justifyContent: 'center', alignItems: 'center', width: 200, height: 200 }}>
            <Box
                sx={{
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    position: 'absolute',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                {src ? (
                    !duration ? (
                        <Skeleton
                            variant='circular'
                            sx={{
                                width: ICON_SIZE.width,
                                height: ICON_SIZE.height,
                            }}
                            aria-label='skeleton'
                        />
                    ) : (
                        <IconButton
                            onClick={() => {
                                toggle();
                            }}
                            sx={{
                                width: ICON_SIZE.width,
                                height: ICON_SIZE.height,
                            }}
                        >
                            {!playing ? (
                                <PlayCircleIcon
                                    sx={{
                                        width: ICON_SIZE.width,
                                        height: ICON_SIZE.height,
                                    }}
                                    data-testid='PlayCircleIcon'
                                />
                            ) : (
                                <StopCircleIcon
                                    sx={{
                                        width: ICON_SIZE.width,
                                        height: ICON_SIZE.height,
                                    }}
                                    data-testid='StopCircleIcon'
                                />
                            )}
                            <CircularProgress
                                variant='determinate'
                                value={(progress as number) / PROGRESS_LACING_RATE}
                                size={ICON_SIZE.progress}
                                sx={{
                                    position: 'absolute',
                                    '.MuiCircularProgress-circle': {
                                        transition: 'none',
                                    },
                                }}
                            />
                        </IconButton>
                    )
                ) : (
                    <HelpIcon sx={{ width: ICON_SIZE.width, height: ICON_SIZE.height }} data-testid='HelpIcon' />
                )}
            </Box>
        </Box>
    );
}
